import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Button,
} from '@mui/material';
import KitchenIcon from '@mui/icons-material/Kitchen';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getKitchenItemsScreen } from '../../../../services/Admin/OrderProcessingScreensApi';
import { formatTime12Hour } from '../../../../utils/dateTimeFormat';

const PreparingItemsScreen = () => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchPreparingItems();
  }, []);

  const fetchPreparingItems = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await getKitchenItemsScreen();

      const apiData = response?.data;

      let groupedItems = {};

      if (Array.isArray(apiData)) {
        groupedItems = apiData.reduce((acc, order) => {
          const items = Array.isArray(order?.order_items)
            ? order.order_items
            : [];

          acc[order.id] = items.map((item) => ({
            ...item,
            order,
          }));

          return acc;
        }, {});
      } else if (apiData && typeof apiData === 'object') {
        groupedItems = Object.entries(apiData).reduce((acc, [orderId, items]) => {
          acc[orderId] = Array.isArray(items) ? items : [];
          return acc;
        }, {});
      }

      setData(groupedItems);
    } catch (err) {
      console.error('خطأ في جلب العناصر قيد التجهيز:', err);
      setError(err.message || 'حدث خطأ في جلب البيانات');
    } finally {
      setLoading(false);
    }
  };

  const getStatusChip = (status) => {
    if (status === 'in_prepration' || status === 'in_preparation') {
      return <Chip size="small" color="warning" label="في التحضير" />;
    }
    if (status === 'ready') {
      return <Chip size="small" color="success" label="جاهز" />;
    }
    if (status === 'pickup') {
      return <Chip size="small" color="success" label="تم الاستلام من العميل" />;
    }
    if (status === 'pending') {
      return <Chip size="small" color="error" label="جديد لم يحضر" />;
    }
    return <Chip size="small" color="default" label={status || '-'} />;
  };

  const getOrderTypeChip = (orderType) => {
    const types = {
      tables: { label: 'طاولات', color: 'primary' },
      delivery: { label: 'توصيل', color: 'secondary' },
      takeaway: { label: 'استلام', color: 'info' },
    };
    
    const type = types[orderType] || { label: orderType, color: 'default' };
    return <Chip size="small" label={type.label} color={type.color} variant="outlined" />;
  };

  const formatTime = (timeValue) => {
    return formatTime12Hour(timeValue);
  };

  const orderEntries = Object.entries(data || {});

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box dir="rtl" sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <KitchenIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            العناصر قيد التجهيز
          </Typography>
        </Box>
        
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchPreparingItems}
          disabled={loading}
        >
          تحديث
        </Button>
      </Box>

      {orderEntries.length > 0 ? (
        <Grid container spacing={3}>
          {orderEntries.map(([orderId, items]) => {
            const firstItem = items?.[0];
            const order = firstItem?.order || {};
            const orderType = order?.order_type || '-';
            const orderStatus = order?.status || '-';
            const openedAt = order?.open_time || firstItem?.open_time;
            const customerName = order?.customer?.name || order?.customer_name;

            return (
              <Grid item xs={12} md={6} lg={4} key={orderId}>
                <Paper 
                  sx={{ 
                    p: 2.5, 
                    height: '100%', 
                    border: '2px solid', 
                    borderColor: 'warning.light',
                    backgroundColor: 'background.paper',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      طلب #{orderId}
                    </Typography>
                    {getOrderTypeChip(orderType)}
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccessTimeIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        وقت الفتح: {formatTime(openedAt)}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        حالة الطلب: 
                      </Typography>
                      <Chip size="small" label={orderStatus} variant="outlined" />
                    </Box>

                    {customerName && (
                      <Typography variant="body2" color="text.secondary">
                        العميل: {customerName}
                      </Typography>
                    )}
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {(items || []).map((item) => {
                      const itemName = item?.product?.name || item?.drinks?.name || 'عنصر بدون اسم';
                      const itemImage = item?.product?.image;

                      return (
                        <Paper
                          key={item.id}
                          variant="outlined"
                          sx={{ 
                            p: 1.5, 
                            backgroundColor: item.status === 'in_prepration' ? '#fff3cd' : 'background.default',
                            borderLeft: '4px solid',
                            borderLeftColor: item.status === 'in_prepration' ? 'warning.main' : 'grey.300',
                          }}
                        >
                          <Box sx={{ display: 'flex', gap: 1.5 }}>
                            {itemImage && (
                              <Box
                                component="img"
                                src={`${process.env.REACT_APP_MEDIA_BASE_URL || ''}/${itemImage}`}
                                alt={itemName}
                                sx={{
                                  width: 60,
                                  height: 60,
                                  objectFit: 'cover',
                                  borderRadius: 1,
                                }}
                              />
                            )}
                            
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                  {itemName}
                                </Typography>
                                {getStatusChip(item.status)}
                              </Box>

                              <Typography variant="body2" color="text.secondary">
                                الكمية: {item.quantity || '-'}
                              </Typography>

                              <Typography variant="body2" color="text.secondary">
                                السعر: {item.price || '-'}
                              </Typography>

                              <Typography variant="body2" color="text.secondary">
                                الإجمالي: {item.total || '-'}
                              </Typography>

                              {item.open_time && (
                                <Typography variant="caption" color="text.secondary">
                                  بدأ التحضير: {formatTime(item.open_time)}
                                </Typography>
                              )}

                              {item.note && (
                                <Paper sx={{ p: 1, mt: 1, backgroundColor: '#e3f2fd' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    ملاحظة: {item.note}
                                  </Typography>
                                </Paper>
                              )}
                            </Box>
                          </Box>
                        </Paper>
                      );
                    })}
                  </Box>

                  <Divider sx={{ my: 1.5 }} />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      عدد العناصر: {items?.length || 0}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      ID الطلب: {orderId}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <KitchenIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            لا توجد عناصر قيد التجهيز حالياً
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            جميع الطلبات قد تم إنجازها
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default PreparingItemsScreen;
